import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Linkedin, Facebook, Loader2, Send, Link2, Unlink, AlertTriangle, Clock, CalendarClock, Trash2, CheckCircle2, XCircle, History, CalendarDays, ListChecks, Upload, X, ThumbsUp, MessageSquare, Share2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ScheduleCalendar from "./ScheduleCalendar";
import { api, uploadImage, mediaUrl } from "../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";

const SCOPES = {
  linkedin: "openid profile email w_member_social w_organization_social r_organization_admin",
  facebook: "public_profile,email,pages_show_list,pages_manage_posts,pages_read_engagement",
};

const STATUS = {
  published: { label: "Publicada", cls: "text-violet", Icon: CheckCircle2 },
  scheduled: { label: "Programada", cls: "text-amber-600", Icon: Clock },
  failed: { label: "Falló", cls: "text-destructive", Icon: XCircle },
};

function startOAuth(provider, clientId) {
  const redirect = `${window.location.origin}/oauth/${provider}/callback`;
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  sessionStorage.setItem(`oauth_state_${provider}`, state);
  const scope = SCOPES[provider];
  const url = provider === "linkedin"
    ? `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirect)}&state=${state}&scope=${encodeURIComponent(scope)}`
    : `https://www.facebook.com/v19.0/dialog/oauth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirect)}&state=${state}&scope=${encodeURIComponent(scope)}`;
  window.location.href = url;
}

function NetworkCard({ provider, Icon, brand, cfg, conn, onDisconnect }) {
  const configured = cfg?.configured;
  const connected = conn?.connected;
  return (
    <div className="card-surface p-5" data-testid={`social-card-${provider}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: brand }}><Icon className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-medium capitalize">{provider}</div>
          {connected ? <div className="text-[11px] text-violet">Conectado · {conn.name}</div>
            : <div className="text-[11px] text-navy/40">Sin conectar</div>}
        </div>
      </div>
      {!configured ? (
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid={`social-notconfigured-${provider}`}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> El administrador aún no ha configurado las credenciales de {provider} en OMNIA.
        </div>
      ) : connected ? (
        <div className="space-y-2">
          <div className="text-xs text-navy/50">Destinos disponibles:</div>
          <ul className="space-y-1">
            {conn.targets.map((t) => (
              <li key={t.urn || t.id} className="text-sm text-navy/75 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-violet" /> {t.label || t.name}</li>
            ))}
          </ul>
          <button onClick={() => onDisconnect(provider)} className="btn btn-outline text-sm mt-2" data-testid={`social-disconnect-${provider}`}><Unlink className="w-3.5 h-3.5" /> Desconectar</button>
        </div>
      ) : (
        <button onClick={() => startOAuth(provider, cfg.client_id || cfg.app_id)} className="btn btn-primary w-full" data-testid={`social-connect-${provider}`}>
          <Link2 className="w-4 h-4" /> Conectar {provider}
        </button>
      )}
    </div>
  );
}

function MetricPill({ label, value }) {
  return <div className="flex-1 text-center rounded-lg bg-white border border-navy/10 py-2"><div className="font-editorial text-2xl text-violet">{value}</div><div className="text-[11px] text-navy/45">{label}</div></div>;
}

function PreviewCard({ provider, author, text, image }) {
  const isLi = provider === "linkedin";
  const brand = isLi ? "#0A66C2" : "#1877F2";
  const Icon = isLi ? Linkedin : Facebook;
  return (
    <div className="rounded-xl border border-navy/10 bg-white overflow-hidden max-w-md" data-testid={`social-preview-${provider}`}>
      <div className="flex items-center gap-2.5 p-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: brand }}><Icon className="w-4 h-4" /></div>
        <div className="leading-tight">
          <div className="text-sm font-medium">{author || (isLi ? "Tu perfil" : "Tu página")}</div>
          <div className="text-[10px] text-navy/40">{isLi ? "Ahora · LinkedIn" : "Ahora · Facebook"}</div>
        </div>
      </div>
      {text && <div className="px-3 pb-2 text-sm text-navy/80 whitespace-pre-wrap">{text}</div>}
      {image && <img src={image} alt="" className="w-full max-h-72 object-cover" />}
      <div className="flex items-center gap-6 px-3 py-2 border-t border-navy/5 text-navy/40 text-xs">
        <span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> Me gusta</span>
        <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Comentar</span>
        <span className="flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Compartir</span>
      </div>
    </div>
  );
}

export default function SocialConnect() {
  const [cfg, setCfg] = useState(null);
  const [conn, setConn] = useState(null);
  const [posts, setPosts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [view, setView] = useState("list");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Solo imágenes (JPG, PNG, WEBP, GIF)");
    setUploading(true);
    try { const url = await uploadImage(file); setImageUrl(url); toast.success("Imagen subida"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir la imagen"); }
    finally { setUploading(false); }
  };

  const loadHistory = async () => {
    try {
      const [p, m] = await Promise.all([api.get("/social/posts"), api.get("/social/metrics")]);
      setPosts(p.data); setMetrics(m.data);
    } catch { /* */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const [c, k] = await Promise.all([api.get("/social/config"), api.get("/social/connections")]);
      setCfg(c.data); setConn(k.data);
      await loadHistory();
    } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [sp, setSp] = useSearchParams();
  const preloadedRef = useRef(false);
  useEffect(() => {
    const img = sp.get("image");
    const txt = sp.get("text");
    if ((img || txt) && !preloadedRef.current) {
      preloadedRef.current = true;
      if (img) setImageUrl(img);
      if (txt) setText(txt);
      setView("list");
      toast.success("Contenido cargado desde Events Marketing");
      setSp({}, { replace: true });
    }
    // eslint-disable-next-line
  }, [sp]);

  const disconnect = async (provider) => {
    try { const r = await api.delete(`/social/${provider}`); setConn(r.data); toast.success(`${provider} desconectado`); } catch { toast.error("Error al desconectar"); }
  };
  const deletePost = async (id) => {
    try { await api.delete(`/social/posts/${id}`); loadHistory(); } catch { /* */ }
  };

  const targetOptions = [];
  (conn?.linkedin?.targets || []).forEach((t) => targetOptions.push({ value: `linkedin::${t.urn}`, label: `LinkedIn · ${t.label}` }));
  (conn?.facebook?.targets || []).forEach((t) => targetOptions.push({ value: `facebook::${t.id}`, label: `Facebook · ${t.name}` }));

  const publish = async () => {
    if (selected.length === 0) return toast.error("Elige al menos un destino");
    if (!text.trim()) return toast.error("Escribe el contenido");
    let scheduled_at = null;
    if (scheduleOn) {
      if (!scheduleAt) return toast.error("Elige fecha y hora");
      const when = new Date(scheduleAt);
      if (when.getTime() <= Date.now()) return toast.error("La fecha debe ser futura");
      scheduled_at = when.toISOString();
    }
    setPublishing(true);
    try {
      const r = await api.post("/social/publish", { targets: selected, text, image_url: imageUrl ? mediaUrl(imageUrl) : null, scheduled_at });
      const st = r.data.status;
      if (st === "scheduled") toast.success(`¡Programado en ${selected.length} red(es)!`);
      else if (st === "published") toast.success(`¡Publicado en ${selected.length} red(es)!`);
      else if (st === "partial") toast.warning("Publicado en algunas redes; otras fallaron. Revisa el historial.");
      else toast.error(r.data.results?.[0]?.error || "No se pudo publicar en ninguna red.");
      setText(""); setImageUrl(""); setScheduleAt(""); setScheduleOn(false); setSelected([]);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo publicar"); } finally { setPublishing(false); }
  };
  const toggleTarget = (v) => setSelected((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  return (
    <OSLayout title="OMNIA Social Connect">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="social-connect-page">
        <div>
          <div className="overline text-navy/40 mb-2">OMNIA People · OMNIA Marketing</div>
          <h2 className="font-editorial text-4xl tracking-tight">OMNIA Social Connect</h2>
          <p className="mt-2 text-navy/50">Vincula tus perfiles de LinkedIn y Páginas de Facebook, publica o programa contenido y mide tu actividad.</p>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <NetworkCard provider="linkedin" Icon={Linkedin} brand="#0A66C2" cfg={cfg?.linkedin} conn={conn?.linkedin} onDisconnect={disconnect} />
              <NetworkCard provider="facebook" Icon={Facebook} brand="#1877F2" cfg={cfg?.facebook} conn={conn?.facebook} onDisconnect={disconnect} />
            </div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-5 space-y-3" data-testid="social-composer">
              <div className="overline text-navy/40">Publicar</div>
              {targetOptions.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-navy/40">Conecta LinkedIn o Facebook (arriba) para publicar o programar.</p>
                  {(text.trim() || imageUrl) && (
                    <div className="rounded-lg border border-violet/20 bg-violet/5 p-3 space-y-2" data-testid="social-preload">
                      <div className="text-xs text-violet font-medium">Contenido listo desde Events Marketing:</div>
                      {imageUrl && <img src={mediaUrl(imageUrl)} alt="" className="max-h-40 rounded-lg" data-testid="social-image-thumb" />}
                      {text.trim() && <textarea className={input} rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="social-text" />}
                      <p className="text-[11px] text-navy/45">Se conservará al conectar una red.</p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-1.5" data-testid="social-targets">
                    <div className="text-xs text-navy/50">Publicar en (elige una o varias):</div>
                    {targetOptions.map((o) => (
                      <label key={o.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${selected.includes(o.value) ? "border-violet bg-violet/5" : "border-navy/10 hover:border-navy/20"}`} data-testid={`social-target-${o.value}`}>
                        <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggleTarget(o.value)} className="accent-violet" />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  <textarea className={input} rows={4} placeholder="¿Qué quieres publicar?" value={text} onChange={(e) => setText(e.target.value)} data-testid="social-text" />
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
                    className="border-2 border-dashed border-navy/15 rounded-lg p-4 text-center transition-colors hover:border-violet/40"
                    data-testid="social-dropzone">
                    {imageUrl ? (
                      <div className="relative inline-block">
                        <img src={mediaUrl(imageUrl)} alt="" className="max-h-40 rounded-lg mx-auto" data-testid="social-image-thumb" />
                        <button onClick={() => setImageUrl("")} className="absolute -top-2 -right-2 bg-white rounded-full border border-navy/10 p-1 shadow-sm hover:text-destructive" data-testid="social-image-remove"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <label className="cursor-pointer text-sm text-navy/50 flex flex-col items-center gap-1.5">
                        {uploading ? <Loader2 className="w-6 h-6 animate-spin text-violet" /> : <Upload className="w-6 h-6 text-navy/30" />}
                        <span>Arrastra una imagen o <span className="text-violet font-medium">haz clic para subir</span></span>
                        <span className="text-[11px] text-navy/35">JPG, PNG, WEBP o GIF · hasta 50 MB</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="social-image-file" />
                      </label>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer" data-testid="social-schedule-toggle">
                      <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} /> <CalendarClock className="w-4 h-4" /> Programar
                    </label>
                    {scheduleOn && (
                      <input type="datetime-local" className={`${input} max-w-[240px]`} value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="social-schedule-at" />
                    )}
                    <button onClick={publish} disabled={publishing} className="btn btn-primary ml-auto" data-testid="social-publish-btn">
                      {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {scheduleOn ? "Programar" : "Publicar"}
                    </button>
                  </div>

                  {selected.length > 0 && (text.trim() || imageUrl) && (
                    <div className="pt-2" data-testid="social-preview">
                      <div className="text-xs text-navy/50 mb-2">Vista previa</div>
                      <div className="flex flex-wrap gap-4">
                        {selected.map((v) => {
                          const [prov] = v.split("::");
                          const opt = targetOptions.find((o) => o.value === v);
                          const author = opt ? opt.label.replace(/^LinkedIn · |^Facebook · /, "") : "";
                          return <PreviewCard key={v} provider={prov} author={author} text={text} image={imageUrl ? mediaUrl(imageUrl) : null} />;
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>

            {/* Métricas por red */}
            {metrics && metrics.total > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="social-metrics">
                <MetricPill label="Publicadas" value={metrics.linkedin.published + metrics.facebook.published} />
                <MetricPill label="Programadas" value={metrics.linkedin.scheduled + metrics.facebook.scheduled} />
                <MetricPill label="Fallidas" value={metrics.linkedin.failed + metrics.facebook.failed} />
              </div>
            )}

            {/* Historial / Calendario */}
            <div className="card-surface p-5" data-testid="social-history">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 overline text-navy/40"><History className="w-3.5 h-3.5" /> Publicaciones</div>
                <div className="flex gap-1 bg-navy/5 rounded-lg p-0.5" data-testid="social-view-tabs">
                  <button onClick={() => setView("list")} className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-white text-navy shadow-sm" : "text-navy/50"}`} data-testid="social-view-list"><ListChecks className="w-3.5 h-3.5" /> Historial</button>
                  <button onClick={() => setView("calendar")} className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${view === "calendar" ? "bg-white text-navy shadow-sm" : "text-navy/50"}`} data-testid="social-view-calendar"><CalendarDays className="w-3.5 h-3.5" /> Calendario</button>
                </div>
              </div>
              {view === "calendar" ? <ScheduleCalendar posts={posts} /> :
                posts.length === 0 ? (
                <p className="text-sm text-navy/40" data-testid="social-history-empty">Aún no has publicado nada.</p>
              ) : (
                <div className="space-y-2">
                  {posts.map((p) => {
                    const st = STATUS[p.status] || STATUS.failed;
                    return (
                      <div key={p.id} className="flex items-start gap-3 border-b border-navy/5 last:border-0 pb-2" data-testid={`social-post-${p.id}`}>
                        <st.Icon className={`w-4 h-4 shrink-0 mt-0.5 ${st.cls}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-navy/80 truncate">{p.text}</div>
                          <div className="text-[11px] text-navy/40 flex items-center gap-2 flex-wrap">
                            <span className="capitalize">{p.provider}</span> · {p.target_label}
                            <span className={st.cls}>· {st.label}</span>
                            {p.status === "scheduled" && p.scheduled_at && <span>· {new Date(p.scheduled_at).toLocaleString("es-MX")}</span>}
                            {p.status === "published" && p.published_at && <span>· {new Date(p.published_at).toLocaleString("es-MX")}</span>}
                            {p.error && <span className="text-destructive">· {p.error}</span>}
                          </div>
                        </div>
                        <button onClick={() => deletePost(p.id)} className="text-navy/25 hover:text-destructive shrink-0" data-testid={`social-post-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </OSLayout>
  );
}
